import { Box, Stack, Typography, useTheme, Tooltip, Chip } from '@mui/material';
import React from 'react';

import { mockPortfolios, type PortfolioOverview } from '../../market/mockData.js';
import { marketService } from '../../../services/marketService.js';
import type { WidgetProps } from '../types/index.js';
import { HorizontalPager } from './components/HorizontalPager.tsx';
import { useProfileNavigation } from '../hooks/useProfileNavigation.js';

const formatPercent = (value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;

export const PortfolioOverviewWidget: React.FC<WidgetProps> = ({ widget, onUpdateOutputs, onUpdateState }) => {
  const theme = useTheme();
  const [portfolios, setPortfolios] = React.useState<PortfolioOverview[]>(() => mockPortfolios);
  const [quoteMap, setQuoteMap] = React.useState<Record<string, number>>({}); // symbol -> changePercent
  const { openProfileByTicker } = useProfileNavigation();

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await marketService.getPortfolioOverviews().catch(() => []);
        if (!cancelled && Array.isArray(data) && data.length > 0) {
          const mapped = data.map((p: any) => ({
            id: p.id,
            name: p.name,
            totalValue: Number(p.totalValue),
            changePercent: Number(p.changePercent),
            holdings: Array.isArray(p.holdings) ? p.holdings : [],
          })) as PortfolioOverview[];
          setPortfolios(mapped);
          onUpdateState({ loading: false, data: mapped });
          return;
        }
      } catch {}
      if (!cancelled) {
        setPortfolios(mockPortfolios);
        onUpdateState({ loading: false, data: mockPortfolios });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onUpdateState]);

  const selectedOutput = React.useMemo(() => {
    return widget.outputs.find((output) => output.id === 'selectedPortfolio')?.value as PortfolioOverview | undefined;
  }, [widget.outputs]);

  const [selectedId, setSelectedId] = React.useState<string | null>(selectedOutput?.id ?? null);

  const currencyFormatter = React.useMemo(
    () =>
      new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }),
    []
  );

  React.useEffect(() => {
    onUpdateState({ loading: false, data: portfolios });
  }, [portfolios, onUpdateState]);

  // Live prices for holdings chips
  React.useEffect(() => {
    let cancelled = false;
    const fetchQuotes = async () => {
      try {
        const symbols = Array.from(
          new Set(portfolios.flatMap((p) => (p.holdings || []).map((h) => h.symbol)))
        );
        if (symbols.length === 0) return;
        const data = await marketService.getMarketDataForSymbols(symbols).catch(() => []);
        if (cancelled) return;
        const map: Record<string, number> = {};
        for (const d of data) map[d.symbol] = Number(d.changePercent) || 0;
        setQuoteMap(map);
      } catch {}
    };
    fetchQuotes();
    const id = setInterval(fetchQuotes, 60000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [portfolios]);

  React.useEffect(() => {
    if (!selectedOutput && portfolios.length > 0) {
      if (selectedId !== portfolios[0].id) {
        setSelectedId(portfolios[0].id);
        onUpdateOutputs({ selectedPortfolio: portfolios[0] });
      }
    }
  }, [portfolios, onUpdateOutputs, selectedOutput, selectedId]);

  React.useEffect(() => {
    if (selectedOutput && selectedOutput.id !== selectedId) {
      setSelectedId(selectedOutput.id);
    }
  }, [selectedOutput, selectedId]);

  const handleSelect = React.useCallback(
    (portfolio: PortfolioOverview) => {
      setSelectedId(portfolio.id);
      onUpdateOutputs({ selectedPortfolio: portfolio });
    },
    [onUpdateOutputs]
  );

  const createSymbolClickHandler = React.useCallback(
    (symbol: string) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      openProfileByTicker(symbol);
    },
    [openProfileByTicker]
  );

  const renderStackedBar = (holdings: PortfolioOverview['holdings']) => {
    const colors = [
      theme.palette.primary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.info.main,
      theme.palette.secondary.main,
      theme.palette.error.main,
    ];
    const items = holdings.slice(0, 6);
    const title = items.map(h => `${h.symbol} ${(h.weight * 100).toFixed(0)}%`).join(' • ');
    return (
      <Tooltip title={title} placement="top" arrow>
        <Box sx={{ height: 8, borderRadius: 4, overflow: 'hidden', display: 'flex', width: '100%', mt: 0.5 }}>
          {items.map((h, i) => (
            <Box key={h.symbol} sx={{ width: `${(h.weight * 100).toFixed(2)}%`, backgroundColor: colors[i % colors.length] }} />
          ))}
        </Box>
      </Tooltip>
    );
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Remove internal duplicate title/subtitle to maximize content */}
      <HorizontalPager ariaLabel="Portfolio overview scroller">
        {portfolios.map((portfolio) => {
          const selected = portfolio.id === selectedId;
          const changePositive = portfolio.changePercent >= 0;
          return (
            <Box
              key={portfolio.id}
              onClick={() => handleSelect(portfolio)}
              sx={{
                width: 150,
                minWidth: 150,
                height: 200,
                borderRadius: 1,
                border: '1px solid',
                borderColor: selected ? 'primary.main' : 'divider',
                backgroundColor: selected ? 'action.selected' : 'background.paper',
                p: 1,
                cursor: 'pointer',
                display: 'grid',
                gridTemplateRows: 'auto 1fr auto',
                overflow: 'hidden',
                transition: 'border-color 0.2s ease, background-color 0.2s ease',
                '&:hover': {
                  borderColor: selected ? 'primary.light' : theme.palette.primary.light,
                },
                scrollSnapAlign: 'start',
              }}
            >
              <Box sx={{ minHeight: 58 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {portfolio.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {currencyFormatter.format(portfolio.totalValue)} AUM
                </Typography>
                <Typography
                  variant="body2"
                  color={changePositive ? 'success.main' : 'error.main'}
                  sx={{ fontWeight: 600, mt: 0.5 }}
                >
                  {formatPercent(portfolio.changePercent)}
                </Typography>
              </Box>
              {renderStackedBar(portfolio.holdings)}
              <Tooltip
                title={portfolio.holdings
                  .map(h => `${h.symbol} ${(h.weight * 100).toFixed(0)}% ${formatPercent(h.changePercent)}`)
                  .join(' • ')}
                placement="bottom"
                arrow
              >
                <Stack spacing={0.5} direction="row" sx={{ overflow: 'hidden', flexWrap: 'nowrap' }}>
                  {portfolio.holdings.slice(0, 3).map((holding) => {
                    const cp = quoteMap[holding.symbol];
                    const color = typeof cp === 'number' ? (cp >= 0 ? 'success' : 'error') : 'default';
                    const suffix = typeof cp === 'number' ? ` ${formatPercent(cp)}` : '';
                    return (
                      <Chip
                        key={holding.symbol}
                        size="small"
                        label={`${holding.symbol} ${(holding.weight * 100).toFixed(0)}%${suffix}`}
                        color={color as any}
                        variant="outlined"
                        onClick={createSymbolClickHandler(holding.symbol)}
                        onMouseDown={(event) => event.stopPropagation()}
                        onPointerDown={(event) => event.stopPropagation()}
                        sx={{ maxWidth: '100%', mr: 0.5, cursor: 'pointer' }}
                      />
                    );
                  })}
                </Stack>
              </Tooltip>
            </Box>
          );
        })}
      </HorizontalPager>
    </Box>
  );
};
